import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Services
import { deleteUserAuth } from '@/services/storage/user-auth.service';

// styles
import './style.css';

// images
import sideLogo from '../../../assets/images/logos3.png';
import defaultAvatar from '../../../assets/images/profile.png';

// graphql
import { useLogoutMutation, useCurrentUserQuery } from '@/generated/graphql';

// material ui
import { AppBar, Avatar, Box, Button, Divider, IconButton, Menu, MenuItem, Stack, Toolbar, Tooltip, Typography } from '@mui/material';

// icons
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { TbLayoutSidebarRightCollapseFilled } from 'react-icons/tb';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// components
import { ModalEditProfile } from './components/modal-edit-profile';
import { ModalUpdatePassword } from './components/modal-update-password';

// interfaces
import type { ICurrentUserLogin } from '@/shared/interfaces';
import { mapGQLUser } from '@/shared/interfaces';

interface IProps {
  sidebarWidth: number;
  isSidebarCollapsed: boolean;
  setSsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const ITEM_HEIGHT = 48;

export const TopBar = ({ sidebarWidth, isSidebarCollapsed, setSsSidebarCollapsed }: IProps) => {
  const [logoutMutation] = useLogoutMutation();
  const navigate = useNavigate();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [currentUserLogin, setCurrentUserLogin] = useState<ICurrentUserLogin | null>(null);

  const [anchorElProfileOption, setAnchorElProfileOption] = useState<null | HTMLElement>(null);
  const openProfileOption = Boolean(anchorElProfileOption);

  const [openModalEditProfile, setOpenModalEditProfile] = useState(false);
  const [openModalUpdatePassword, setOpenModalUpdatePassword] = useState(false);

  // query

  const currentUserQuery = useCurrentUserQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const user = mapGQLUser(data.me);

      setCurrentUserLogin({
        id: user.id,
        username: user.username,
        role: user.role.name,
        name: user.name,
      });
    },
    onError: (error_resp) => {
      if (error_resp.message.includes('Unauthenticated')) {
        deleteUserAuth();
        navigate('/');
      }
    },
  });

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logout = async () => {
    logoutMutation({
      onCompleted(data) {
        console.log('logout');
      },
    });

    setTimeout(() => {
      deleteUserAuth();
      navigate('/');
    }, 300);
  };

  const handleClickProfileOption = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElProfileOption(event.currentTarget);
  };
  const selectProfileOption = (option: string) => {
    setAnchorElProfileOption(null);
    if (option === 'Edit Profit') {
      setOpenModalEditProfile(true);
      setAnchorElUser(null);
    } else if (option === 'Update Password') {
      setOpenModalUpdatePassword(true);
      setAnchorElUser(null);
    }
  };

  return (
    <>
      <AppBar position="fixed">
        <Toolbar sx={{ pl: '12px !important' }}>
          {!isSidebarCollapsed && (
            <Stack direction={'row'} width={sidebarWidth - 12}>
              <Avatar alt="S3" src={sideLogo} />

              <Stack direction={'column'} alignSelf={'center'} justifyContent={'start'} display={'flex'} ml={1}>
                <Typography fontSize={12} fontWeight={900} lineHeight={'1.2'}>
                  MANAGEMENT
                </Typography>
                <Typography fontSize={12} fontWeight={900}>
                  SYSTEM
                </Typography>
              </Stack>
            </Stack>
          )}
          <Stack direction={'row'} sx={{ flexGrow: 1 }}>
            {!isSidebarCollapsed ? (
              <IconButton onClick={() => setSsSidebarCollapsed(true)} size="large" edge="start" color="inherit" aria-label="menu">
                <MenuOpenIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => setSsSidebarCollapsed(false)}
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ pl: 2 }}>
                <TbLayoutSidebarRightCollapseFilled />
              </IconButton>
            )}
          </Stack>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} className="accountAvatar">
                <Avatar alt="Remy Sharp" src={defaultAvatar} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorElUser}
              id="account-menu"
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              slotProps={{
                paper: {
                  // Target the Paper slot
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    minWidth: 300,
                    '& .MuiAvatar-root': {
                      width: 50,
                      height: 50,
                      // ml: -0.5, // No longer needed with Box for layout
                      // mr: 1.5,
                    },
                    '&:before': {
                      // The arrow
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
              <Box sx={{ padding: '0px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction={'row'}>
                  <Avatar src={defaultAvatar} sx={{ mr: 1.5 }}></Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '15px' }}>{currentUserLogin?.name}</Typography>
                    <Typography sx={{ fontSize: '12px' }} color="text.secondary">
                      {currentUserLogin?.username}
                    </Typography>
                  </Box>
                </Stack>
                <div>
                  <IconButton
                    aria-label="more"
                    id="long-button"
                    aria-controls={openProfileOption ? 'long-menu' : undefined}
                    aria-expanded={openProfileOption ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleClickProfileOption}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="long-menu"
                    anchorEl={anchorElProfileOption}
                    open={openProfileOption}
                    onClose={() => setAnchorElProfileOption(null)}
                    slotProps={{
                      list: {
                        'aria-labelledby': 'long-button',
                      },
                      paper: {
                        style: {
                          maxHeight: ITEM_HEIGHT * 4.5,
                          width: '20ch',
                        },
                      },
                    }}>
                    <MenuItem key="Edit Profit" onClick={() => selectProfileOption('Edit Profit')}>
                      Edit Profit
                    </MenuItem>
                    <MenuItem key="Update Password" onClick={() => selectProfileOption('Update Password')}>
                      Update Password
                    </MenuItem>
                  </Menu>
                </div>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box sx={{ p: '0px 16px' }} justifyContent={'right'} display={'flex'}>
                {' '}
                {/* Padding around the button */}
                <Button
                  onClick={logout}
                  variant="outlined" // This gives the border and style similar to your screenshot
                  startIcon={<LogoutIcon />}
                  sx={{
                    justifyContent: 'flex-start', // Aligns icon and text to the left
                    textTransform: 'none', // Prevents uppercase text
                    color: 'primary.main', // Or your desired button text color
                    borderColor: 'rgba(0, 0, 0, 0.23)', // Default outlined button border
                    '&:hover': {
                      // Define hover styles if needed
                    },
                  }}>
                  Sign Out
                </Button>
              </Box>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <ModalEditProfile
        open={openModalEditProfile}
        setOpen={setOpenModalEditProfile}
        currentUserLogin={currentUserLogin}
        refetch={currentUserQuery.refetch}
      />
      <ModalUpdatePassword open={openModalUpdatePassword} setOpen={setOpenModalUpdatePassword} currentUserLogin={currentUserLogin} />
    </>
  );
};
